import React          from 'react';
import PropTypes      from 'prop-types';
import Slider         from 'react-slick';
import SlickSliderDot from './SlickSliderDot';

class SlickSlider extends React.Component {

	constructor(props) {
		super(props);

		this.state = {
			allowAutoplay:    true,
			animateDot:       null,
			animationTimeout: null,
			pauseDot:         null,
			slideTouch:       null,
		};

		this.slider = null;

		this.getInnerSlider    = this.getInnerSlider.bind(this);
		this.pauseAutoplay     = this.pauseAutoplay.bind(this);
		this.resumeAutoplay    = this.resumeAutoplay.bind(this);
		this.animateNextDot    = this.animateNextDot.bind(this);
		this.clearDotAnimation = this.clearDotAnimation.bind(this);
		this.afterChange       = this.afterChange.bind(this);
		this.beforeChange      = this.beforeChange.bind(this);
		this.customPaging      = this.customPaging.bind(this);
		this.slideTouchStart   = this.slideTouchStart.bind(this);
		this.slideTouchEnd     = this.slideTouchEnd.bind(this);
		this.slideTouchMove    = this.slideTouchMove.bind(this);
	}

	componentDidMount() {
		const innerSlider = this.getInnerSlider();

		if (innerSlider) {
			/* make sure the initial countdown animation starts after initialization */
			if (this.slider.props.autoplay) {
				const initializationInterval = setInterval(() => {
					if (innerSlider.state.autoPlayTimer) {
						this.animateNextDot();
						clearInterval(initializationInterval);
					}
				}, 10);
			}

			/* hook changeSlide event to be notified about click on dots */
			const changeSlide       = innerSlider.changeSlide;
			innerSlider.changeSlide = (options) => {
				if (options.message === 'dots') {
					this.pauseAutoplay();
				}

				return changeSlide(options);
			};

			/* hook swipeEnd event to be notified about click on dots */
			const swipeEnd       = innerSlider.swipeEnd;
			innerSlider.swipeEnd = (e) => {
				this.pauseAutoplay();

				return swipeEnd(e);
			};

			/* hook autoPlay in order to correctly handle paused autoplay */
			const autoPlay       = innerSlider.autoPlay;
			innerSlider.autoPlay = () => {
				if (!this.state.animationTimeout) {
					setTimeout(() => {
						autoPlay();
					}, 0);
				}
			};
		}

		/* apply event handlers for touch to lock scroll if necessary */
		if (this.props.lockScrollDirection) {
			const slides = this.slider.querySelector('.slick-slide');

			slides.addEventListener('touchstart', this.slideTouchStart);
			slides.addEventListener('touchend', this.slideTouchEnd);
			slides.addEventListener('touchmove', this.slideTouchMove);
		}
	}

	componentWillUnmount() {
		this.setState({
			allowAutoplay: false
		});
	}

	getInnerSlider() {
		return this.slider ? this.slider.innerSlider : null;
	}

	pauseAutoplay() {
		if (this.slider.props.autoplay) {
			const innerSlider = this.getInnerSlider();

			clearTimeout(this.state.animationTimeout);

			if (innerSlider && this.props.autoplayPause) {
				this.clearDotAnimation();
				innerSlider.pause();
				this.setState({
					animationTimeout: setTimeout(() => {
						this.resumeAutoplay();
					}, this.props.autoplayPause)
				});
			}
		}
	}

	resumeAutoplay() {
		if (this.state.allowAutoplay) {
			clearTimeout(this.state.animationTimeout);
			this.setState({
				animationTimeout: null
			});

			this.getInnerSlider().autoPlay();
			this.animateNextDot();
		}
	}

	animateNextDot(currentSlide) {
		if (this.slider && this.slider.props.autoplay && !this.state.animationTimeout) {
			currentSlide = typeof currentSlide !== 'undefined' ? currentSlide : this.getInnerSlider().state.currentSlide;

			const slideCount  = this.getInnerSlider().state.slideCount;
			const scrollCount = this.getInnerSlider().props.slidesToScroll;
			const dotCount    = Math.ceil(slideCount / scrollCount);
			const dotStep     = scrollCount * (this.slider.props.rtl ? -1 : 1);
			let nextDot       = Math.floor(((currentSlide + dotStep) % slideCount) / scrollCount);

			if (nextDot < 0) {
				nextDot += dotCount;
			}

			this.setState({
				animateDot: nextDot,
				pauseDot:   null,
			});
		}
	}

	clearDotAnimation() {
		/* do not use setState to avoid re-rendering */
		/* eslint-disable */
		this.state.animateDot = null;
		this.state.pauseDot   = null;
		/* eslint-enable */
	}

	afterChange(targetSlide) {
		this.setState({
			slideTouch: null
		});

		if (this.props.afterChange) {
			this.props.afterChange(targetSlide);
		}
	}

	beforeChange(currentSlide, targetSlide) {
		if (this.props.beforeChange) {
			this.props.beforeChange(currentSlide, targetSlide);
		}

		this.animateNextDot(targetSlide);
	}

	customPaging(i) {
		let dot;
		if (this.props.customPaging) {
			dot = this.props.customPaging(i);
		} else {
			dot = <SlickSliderDot animateDot={this.state.animateDot} pauseDot={this.state.pauseDot} index={i}/>;
		}
		return dot;
	}

	slideTouchStart(e) {
		this.setState({
			slideTouch: {x: e.touches[0].clientX, y: e.touches[0].clientY, lock: false}
		});
	}

	slideTouchEnd() {
		this.setState({
			slideTouch: null
		});
	}

	slideTouchMove(e) {
		if (this.state.slideTouch) {
			if (this.state.slideTouch.lock) {
				if (this.state.slideTouch.lock === 'x') {
					e.preventDefault();
				} else {
					e.stopPropagation();
				}
			} else {
				const diffX = Math.abs(this.state.slideTouch.x - e.touches[0].clientX);
				const diffY = Math.abs(this.state.slideTouch.y - e.touches[0].clientY);

				if (diffY > diffX) {
					e.stopPropagation();
					this.setState({
						slideTouch: {
							lock: 'y'
						}
					});
				} else {
					this.setState({
						slideTouch: {
							lock: 'x'
						}
					});
				}
			}
		}
	}

	render() {
		const isSingleElementStatic = React.Children.count(this.props.children) === 1 && !this.props.singleElementSlider;

		// temporary hack in order to override bug in react-slick > 0.14.4
		setTimeout(() => {
			if (this.slider && this.slider.props.autoplay && !this.getInnerSlider().state.autoPlayTimer) {
				this.getInnerSlider().autoPlay();
			}
		}, 25);

		const sliderConfig = {
			adaptiveHeight: this.props.adaptiveHeight,
			afterChange:    this.afterChange,
			arrows:         this.props.arrows,
			autoplay:       this.props.autoplay && !isSingleElementStatic,
			autoplaySpeed:  this.props.autoplaySpeed,
			beforeChange:   this.beforeChange,
			className:      this.props.className,
			customPaging:   this.customPaging,
			dots:           this.props.dots && !isSingleElementStatic,
			infinite:       this.props.infinite && !isSingleElementStatic,
			lazyLoad:       this.props.lazyLoad,
			nextArrow:      this.props.nextArrow,
			prevArrow:      this.props.prevArrow,
			rtl:            this.props.rtl,
			slidesToShow:   this.props.slidesToShow,
			slidesToScroll: this.props.slidesToScroll,
			speed:          this.props.speed,
			swipe:          !isSingleElementStatic,
			variableWidth:  this.props.variableWidth,
			responsive:     this.props.responsive,
		};

		return (
			<Slider ref={(sliderRef) => {
				this.slider = sliderRef;
			}} {...sliderConfig}
			>
				{this.props.children}
			</Slider>
		);
	}
}

SlickSlider.propTypes = {
	adaptiveHeight:      PropTypes.bool,
	afterChange:         PropTypes.func,
	arrows:              PropTypes.bool,
	autoplay:            PropTypes.bool,
	autoplayPause:       PropTypes.number,
	autoplaySpeed:       PropTypes.number,
	beforeChange:        PropTypes.func,
	children:            PropTypes.node,
	className:           PropTypes.string,
	customPaging:        PropTypes.func,
	dots:                PropTypes.bool,
	infinite:            PropTypes.bool,
	lazyLoad:            PropTypes.bool,
	lockScrollDirection: PropTypes.bool,
	nextArrow:           PropTypes.node,
	prevArrow:           PropTypes.node,
	rtl:                 PropTypes.bool,
	singleElementSlider: PropTypes.bool,
	slidesToShow:        PropTypes.number,
	slidesToScroll:      PropTypes.number,
	speed:               PropTypes.number,
	variableWidth:       PropTypes.bool,
	responsive:          PropTypes.array,
};

SlickSlider.defaultProps = {
	adaptiveHeight:      false,
	afterChange:         null,
	arrows:              false,
	autoplay:            false,
	autoplayPause:       0,
	autoplaySpeed:       6000,
	beforeChange:        null,
	className:           '',
	customPaging:        null,
	dots:                true,
	infinite:            true,
	lazyLoad:            false,
	lockScrollDirection: false,
	rtl:                 false,
	singleElementSlider: true,
	slidesToShow:        1,
	slidesToScroll:      1,
	speed:               1000,
	variableWidth:       false,
	responsive:          []
};

export default SlickSlider;
